import { useState, useCallback, useEffect } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { useTranslation } from "react-i18next";
import { Container } from "../../../shared/ui/container";
import { ReelCard } from "../../reels/components/reel-card";
import { ReelsModal } from "../../reels/components/reels-modal";
import { reelsApi } from "../../reels/api/reels.api";
import { reelsKeys } from "../../reels/constants/query-keys";

import { Icon } from "@majourneys/icons";
export function ReelsSection() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const { data } = useQuery({
    queryKey: reelsKeys.list({ lang: i18n.language }),
    queryFn: () => reelsApi.list({ lang: i18n.language }),
    staleTime: 60_000,
  });
  const reels = data?.data ?? [];
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: true,
    direction: isRtl ? "rtl" : "ltr",
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  if (reels.length === 0) return null;

  return (
    <section className="py-10 md:py-16">
      <Container>
        {/*
         * One row at every width. This used to stack on phones, so "View all"
         * and the arrows landed on a line of their own under the title and
         * pushed the rail further down the screen. The title is short enough
         * now to sit beside them, and the controls belong at the end of the
         * header row where the eye looks for them.
         */}
        <div className="mb-5 flex flex-row items-end justify-between gap-4 md:mb-6">
          <div className="min-w-0">
            <p className="font-sans text-[11px] font-bold uppercase tracking-[0.14em] text-[#C4005E]">
              {t("reels.eyebrow", "Videos")}
            </p>
            <h2 className="font-heading text-[26px] font-bold leading-[1.15] tracking-[-0.01em] text-text sm:text-[30px] md:text-[36px]">
              {t("reels.section_title", "Watch & book")}
            </h2>
          </div>
          {/* `shrink-0`: a long translation squeezes the title, never the
              controls. */}
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/$lang/reels"
              params={{ lang: lang ?? "en" }}
              className="me-1 font-sans text-[13px] font-bold text-[#040033] transition hover:text-[#E5006E] hover:underline md:text-[14px]"
            >
              {t("reels.view_all", "View all")}
            </Link>
            <button
              type="button"
              aria-label={t("common.previous")}
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canPrev}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#040033]/15 bg-white text-[#040033] transition hover:border-[#E5006E]/30 hover:text-[#E5006E] disabled:opacity-40 md:h-10 md:w-10"
            >
              <Icon name={isRtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} className={`text-xl`} />
            </button>
            <button
              type="button"
              aria-label={t("common.next")}
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNext}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#040033]/15 bg-white text-[#040033] transition hover:border-[#E5006E]/30 hover:text-[#E5006E] disabled:opacity-40 md:h-10 md:w-10"
            >
              <Icon name={isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} className={`text-xl`} />
            </button>
          </div>
        </div>

        <div className="-my-3 overflow-hidden py-3" ref={emblaRef}>
          <div className="flex gap-3 md:gap-4">
            {reels.map((reel, i) => (
              <div
                key={reel.id}
                 className="min-w-0 flex-[0_0_72%] sm:flex-[0_0_42%] md:flex-[0_0_29%] lg:flex-[0_0_23%] xl:flex-[0_0_19%]"
              >
                <ReelCard reel={reel} onClick={() => setActiveIndex(i)} />
              </div>
            ))}
          </div>
        </div>
      </Container>

      {activeIndex !== null && (
        <ReelsModal
          reels={reels}
          activeIndex={activeIndex}
          onClose={() => setActiveIndex(null)}
          onNavigate={setActiveIndex}
        />
      )}
    </section>
  );
}
